import React, { useState } from 'react';
import { PlusIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { FileUpload } from './ui/FileUpload';
import { Modal } from './ui/Modal';
import { Order, OrderType, Lender, PayoffCompany, InsuranceCompany, AgentContact, TitleCompany, TitleContact } from '../types';
export interface OrderFormProps {
  order?: Order;
  lenders: Lender[];
  payoffCompanies: PayoffCompany[];
  insuranceCompanies: InsuranceCompany[];
  agentContacts: AgentContact[];
  titleCompanies: TitleCompany[];
  titleContacts: TitleContact[];
  onSubmit: (orderData: Partial<Order>) => void;
  onCancel: () => void;
  onCreatePayoffCompany?: (company: Omit<PayoffCompany, 'id'>) => void;
  onCreateAgentContact?: (contact: Omit<AgentContact, 'id'>) => void;
  onCreateTitleCompany?: (company: Omit<TitleCompany, 'id'>) => void;
  onCreateInsuranceCompany?: (company: Omit<InsuranceCompany, 'id'>) => void;
}
export function OrderForm({
  order,
  lenders,
  payoffCompanies,
  insuranceCompanies,
  agentContacts,
  titleCompanies,
  titleContacts,
  onSubmit,
  onCancel,
  onCreatePayoffCompany,
  onCreateAgentContact,
  onCreateTitleCompany,
  onCreateInsuranceCompany
}: OrderFormProps) {
  const [orderType, setOrderType] = useState<OrderType>(order?.orderType || 'payoff');
  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
  const [showNewAgentModal, setShowNewAgentModal] = useState(false);
  const [showNewTitleCompanyModal, setShowNewTitleCompanyModal] = useState(false);
  const [showNewInsuranceCompanyModal, setShowNewInsuranceCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    phone: '',
    email: '',
    isAutomated: false
  });
  const [newAgent, setNewAgent] = useState({
    name: '',
    phone: '',
    email: '',
    companyId: ''
  });
  const [newTitleCompany, setNewTitleCompany] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });
  const [newInsuranceCompany, setNewInsuranceCompany] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [borrowersAuthFile, setBorrowersAuthFile] = useState<File | null>(null);
  const [insuranceAuthFile, setInsuranceAuthFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    loanNumber: order?.loanNumber || '',
    borrowerName: order?.borrowerName || '',
    assignedTo: order?.assignedTo || '',
    loanType: order?.loanType || 'Refinance',
    chosenLenderId: order?.chosenLenderId || '',
    // Payoff
    payoffOrderedDate: order?.payoffOrderedDate || new Date().toISOString().split('T')[0],
    payoffCompletedDate: order?.payoffCompletedDate || '',
    goodThroughDate: order?.goodThroughDate || '',
    existingLoanNumber: order?.existingLoanNumber || '',
    lienPosition: order?.lienPosition || '1st',
    liabilityType: order?.liabilityType || 'Real Estate',
    payoffCompanyId: order?.payoffCompanyId || '',
    payoffCompanyPhone: order?.payoffCompanyPhone || '',
    payoffCompanyEmail: order?.payoffCompanyEmail || '',
    isAutomated: order?.isAutomated || false,
    borrowersAuthAttached: order?.borrowersAuthAttached || false,
    // Insurance
    insuranceType: order?.insuranceType || 'HOI',
    insuranceCompanyId: order?.insuranceCompanyId || '',
    agentContactId: order?.agentContactId || '',
    agentPhone: order?.agentPhone || '',
    agentEmail: order?.agentEmail || '',
    mortgageeClause: order?.mortgageeClause || '',
    hoiEffectiveDate: order?.hoiEffectiveDate || '',
    policyNumber: order?.policyNumber || '',
    insuranceBorrowersAuthAttached: order?.insuranceBorrowersAuthAttached || false,
    // Title
    borrowerDob: order?.borrowerDob || '',
    borrowerLast4SSN: order?.borrowerLast4SSN || '',
    titleCompanyId: order?.titleCompanyId || '',
    titleContactId: order?.titleContactId || '',
    titleContactPhone: order?.titleContactPhone || '',
    titleContactEmail: order?.titleContactEmail || '',
    titleMortgageeClause: order?.titleMortgageeClause || '',
    titleChosenLenderId: order?.chosenLenderId || '',
    // Appraisal
    appraiser: order?.appraiser || '',
    propertyAddress: order?.propertyAddress || '',
    valuLinkId: order?.valuLinkId || '',
    appraisalOrderDate: order?.appraisalOrderDate || ''
  });
  // Payoff handlers
  const handlePayoffCompanyChange = (companyId: string) => {
    const company = payoffCompanies.find(c => c.id === companyId);
    setFormData({
      ...formData,
      payoffCompanyId: companyId,
      payoffCompanyPhone: company?.phone || '',
      payoffCompanyEmail: company?.email || '',
      isAutomated: company?.isAutomated || false
    });
  };
  const handleCreateCompany = () => {
    if (onCreatePayoffCompany) {
      onCreatePayoffCompany(newCompany);
    }
    setShowNewCompanyModal(false);
    setNewCompany({
      name: '',
      phone: '',
      email: '',
      isAutomated: false
    });
  };
  const handleBorrowersAuthUpload = (file: File) => {
    setBorrowersAuthFile(file);
    setFormData({
      ...formData,
      borrowersAuthAttached: true
    });
  };
  // Insurance handlers
  const handleAgentContactChange = (contactId: string) => {
    const agent = agentContacts.find(a => a.id === contactId);
    setFormData({
      ...formData,
      agentContactId: contactId,
      agentPhone: agent?.phone || '',
      agentEmail: agent?.email || ''
    });
  };
  const handleChosenLenderChange = (lenderId: string) => {
    const lender = lenders.find(l => l.id === lenderId);
    setFormData({
      ...formData,
      chosenLenderId: lenderId,
      mortgageeClause: lender?.insuranceMortgageeClause || ''
    });
  };
  const handleCreateAgent = () => {
    if (onCreateAgentContact) {
      onCreateAgentContact(newAgent);
    }
    setShowNewAgentModal(false);
    setNewAgent({
      name: '',
      phone: '',
      email: '',
      companyId: ''
    });
  };
  const handleCreateInsuranceCompany = () => {
    if (onCreateInsuranceCompany) {
      onCreateInsuranceCompany(newInsuranceCompany);
    }
    setShowNewInsuranceCompanyModal(false);
    setNewInsuranceCompany({
      name: '',
      phone: '',
      email: ''
    });
  };
  const handleInsuranceAuthUpload = (file: File) => {
    setInsuranceAuthFile(file);
    setFormData({
      ...formData,
      insuranceBorrowersAuthAttached: true
    });
  };
  // Title handlers
  const handleTitleContactChange = (contactId: string) => {
    const contact = titleContacts.find(c => c.id === contactId);
    setFormData({
      ...formData,
      titleContactId: contactId,
      titleContactPhone: contact?.phone || '',
      titleContactEmail: contact?.email || ''
    });
  };
  const handleTitleLenderChange = (lenderId: string) => {
    const lender = lenders.find(l => l.id === lenderId);
    setFormData({
      ...formData,
      titleChosenLenderId: lenderId,
      titleMortgageeClause: lender?.titleMortgageeClause || ''
    });
  };
  const handleCreateTitleCompany = () => {
    if (onCreateTitleCompany) {
      onCreateTitleCompany(newTitleCompany);
    }
    setShowNewTitleCompanyModal(false);
    setNewTitleCompany({
      name: '',
      phone: '',
      email: '',
      address: ''
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const baseData = {
      loanNumber: formData.loanNumber,
      borrowerName: formData.borrowerName,
      assignedTo: formData.assignedTo,
      orderType
    };
    let typeSpecificData = {};
    if (orderType === 'payoff') {
      typeSpecificData = {
        payoffOrderedDate: formData.payoffOrderedDate,
        payoffCompletedDate: formData.payoffCompletedDate,
        goodThroughDate: formData.goodThroughDate,
        existingLoanNumber: formData.existingLoanNumber,
        lienPosition: formData.lienPosition,
        liabilityType: formData.liabilityType,
        payoffCompanyId: formData.payoffCompanyId,
        payoffCompanyPhone: formData.payoffCompanyPhone,
        payoffCompanyEmail: formData.payoffCompanyEmail,
        isAutomated: formData.isAutomated,
        borrowersAuthAttached: formData.borrowersAuthAttached
      };
    } else if (orderType === 'insurance') {
      typeSpecificData = {
        loanType: formData.loanType,
        chosenLenderId: formData.chosenLenderId,
        insuranceType: formData.insuranceType,
        insuranceCompanyId: formData.insuranceCompanyId,
        agentContactId: formData.agentContactId,
        agentPhone: formData.agentPhone,
        agentEmail: formData.agentEmail,
        mortgageeClause: formData.mortgageeClause,
        hoiEffectiveDate: formData.hoiEffectiveDate,
        policyNumber: formData.policyNumber,
        insuranceBorrowersAuthAttached: formData.insuranceBorrowersAuthAttached
      };
    } else if (orderType === 'title') {
      typeSpecificData = {
        chosenLenderId: formData.titleChosenLenderId,
        borrowerDob: formData.borrowerDob,
        borrowerLast4SSN: formData.borrowerLast4SSN,
        titleCompanyId: formData.titleCompanyId,
        titleContactId: formData.titleContactId,
        titleContactPhone: formData.titleContactPhone,
        titleContactEmail: formData.titleContactEmail,
        titleMortgageeClause: formData.titleMortgageeClause
      };
    } else if (orderType === 'appraisal') {
      typeSpecificData = {
        appraiser: formData.appraiser,
        propertyAddress: formData.propertyAddress,
        valuLinkId: formData.valuLinkId,
        appraisalOrderDate: formData.appraisalOrderDate
      };
    }
    onSubmit({
      ...baseData,
      ...typeSpecificData
    });
  };
  const orderTypeOptions = [{
    value: 'payoff',
    label: 'Payoff'
  }, {
    value: 'insurance',
    label: 'Insurance'
  }, {
    value: 'title',
    label: 'Title'
  }, {
    value: 'appraisal',
    label: 'Appraisal'
  }];
  const lienPositionOptions = [{
    value: '1st',
    label: '1st Lien'
  }, {
    value: '2nd',
    label: '2nd Lien'
  }];
  const liabilityTypeOptions = [{
    value: 'Real Estate',
    label: 'Real Estate'
  }, {
    value: 'HELOC',
    label: 'HELOC'
  }, {
    value: 'Solar',
    label: 'Solar'
  }];
  const payoffCompanyOptions = [{
    value: '',
    label: 'Select Payoff Company'
  }, ...payoffCompanies.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const insuranceTypeOptions = [{
    value: 'HOI',
    label: 'Homeowners Insurance (HOI)'
  }, {
    value: 'Flood',
    label: 'Flood Insurance'
  }];
  const loanTypeOptions = [{
    value: 'Refinance',
    label: 'Refinance'
  }, {
    value: 'Purchase',
    label: 'Purchase'
  }];
  const insuranceCompanyOptions = [{
    value: '',
    label: 'Select Insurance Company'
  }, ...insuranceCompanies.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const agentContactOptions = [{
    value: '',
    label: 'Select Agent'
  }, ...agentContacts.map(a => ({
    value: a.id,
    label: a.name
  }))];
  const chosenLenderOptions = [{
    value: '',
    label: 'Select Chosen Lender'
  }, ...lenders.map(l => ({
    value: l.id,
    label: l.name
  }))];
  const lenderOptions = [{
    value: '',
    label: 'Select Lender'
  }, ...lenders.map(lender => ({
    value: lender.id,
    label: lender.name
  }))];
  const titleCompanyOptions = [{
    value: '',
    label: 'Select Title Company'
  }, ...titleCompanies.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const titleContactOptions = [{
    value: '',
    label: 'Select Title Contact'
  }, ...titleContacts.map(c => ({
    value: c.id,
    label: c.name
  }))];
  const titleLenderOptions = [{
    value: '',
    label: 'Select Chosen Lender'
  }, ...lenders.map(l => ({
    value: l.id,
    label: l.name
  }))];
  const effectiveDateLabel = formData.loanType === 'Purchase' ? 'HOI Effective Date (Close Date)' : 'HOI Effective Date (Ordered Date)';
  return <>
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-93">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-94">
          <Select label="Order Type" options={orderTypeOptions} value={orderType} onChange={e => setOrderType(e.target.value as OrderType)} disabled={!!order} data-id="element-95" />
          <Input label="Loan Number" value={formData.loanNumber} onChange={e => setFormData({
          ...formData,
          loanNumber: e.target.value
        })} required data-id="element-96" />
          <Input label="Borrower Name" value={formData.borrowerName} onChange={e => setFormData({
          ...formData,
          borrowerName: e.target.value
        })} required data-id="element-97" />
          <Input label="Assigned To" value={formData.assignedTo} onChange={e => setFormData({
          ...formData,
          assignedTo: e.target.value
        })} required data-id="element-98" />
        </div>

        {/* === PAYOFF SECTION === */}
        {orderType === 'payoff' && <div className="border-t border-slate-200 pt-6" data-id="element-99">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-100">
              Payoff Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-101">
              <Input label="Payoff Ordered Date" type="date" value={formData.payoffOrderedDate} onChange={e => setFormData({
            ...formData,
            payoffOrderedDate: e.target.value
          })} required data-id="element-102" />
              <Input label="Payoff Good Thru Date" type="date" value={formData.goodThroughDate} onChange={e => setFormData({
            ...formData,
            goodThroughDate: e.target.value
          })} data-id="element-103" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-104">
              <Input label="Existing Loan #" value={formData.existingLoanNumber} onChange={e => setFormData({
            ...formData,
            existingLoanNumber: e.target.value
          })} required data-id="element-105" />
              <Select label="Lien Position" options={lienPositionOptions} value={formData.lienPosition} onChange={e => setFormData({
            ...formData,
            lienPosition: e.target.value as '1st' | '2nd'
          })} required data-id="element-106" />
              <Select label="Liability Type" options={liabilityTypeOptions} value={formData.liabilityType} onChange={e => setFormData({
            ...formData,
            liabilityType: e.target.value as any
          })} required data-id="element-107" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-108">
              <div data-id="element-109">
                <div className="flex items-center justify-between mb-1" data-id="element-110">
                  <label className="block text-sm font-medium" style={{
                color: 'var(--nmhl-text-primary)'
              }} data-id="element-111">
                    Payoff Company
                  </label>
                  <button type="button" onClick={() => setShowNewCompanyModal(true)} className="inline-flex items-center text-xs font-medium transition-colors hover:opacity-80" style={{
                color: 'var(--nmhl-burgundy)'
              }} data-id="element-112">
                    <PlusIcon className="w-3 h-3 mr-1" data-id="element-113" />
                    Create New
                  </button>
                </div>
                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
              '--tw-ring-color': 'var(--nmhl-burgundy)'
            } as React.CSSProperties} value={formData.payoffCompanyId} onChange={e => handlePayoffCompanyChange(e.target.value)} required data-id="element-114">
                  {payoffCompanyOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-115">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>
            {formData.payoffCompanyId && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-116">
                <div data-id="element-117">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-118">Company Phone</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-119">
                    {formData.payoffCompanyPhone || '—'}
                  </p>
                </div>
                <div data-id="element-120">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-121">Company Email</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-122">
                    {formData.payoffCompanyEmail || '—'}
                  </p>
                </div>
                <div data-id="element-123">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-124">Is Automated</p>
                  <div className="flex items-center space-x-2" data-id="element-125">
                    {formData.isAutomated ? <span className="inline-flex items-center text-sm font-medium text-emerald-700" data-id="element-126">
                        <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-127" />
                        Yes
                      </span> : <span className="inline-flex items-center text-sm font-medium text-slate-500" data-id="element-128">
                        No
                      </span>}
                  </div>
                </div>
              </div>}
            <div className="mt-4" data-id="element-129">
              <div className="flex items-center justify-between mb-2" data-id="element-130">
                <label className="block text-sm font-medium" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-131">
                  Borrowers Authorization
                </label>
                <span className="text-xs font-medium" style={{
              color: 'var(--nmhl-burgundy)'
            }} data-id="element-132">
                  Required before send
                </span>
              </div>
              {formData.borrowersAuthAttached && !borrowersAuthFile ? <div className="flex items-center space-x-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-133">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-134" />
                  <span className="text-sm font-medium text-emerald-700" data-id="element-135">
                    Authorization attached
                  </span>
                </div> : <div data-id="element-136">
                  <FileUpload onFileSelect={handleBorrowersAuthUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-id="element-137" />
                  {!formData.borrowersAuthAttached && <div className="flex items-center space-x-2 mt-2 text-amber-600" data-id="element-138">
                      <AlertCircleIcon className="w-4 h-4" data-id="element-139" />
                      <span className="text-xs" data-id="element-140">
                        Authorization must be attached before the order can be
                        sent
                      </span>
                    </div>}
                </div>}
            </div>
          </div>}

        {/* === INSURANCE SECTION === */}
        {orderType === 'insurance' && <div className="border-t border-slate-200 pt-6" data-id="element-141">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-142">
              Insurance Details
            </h3>

            {/* Row 1: Type + Loan Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-143">
              <Select label="Insurance Type" options={insuranceTypeOptions} value={formData.insuranceType} onChange={e => setFormData({
            ...formData,
            insuranceType: e.target.value as 'HOI' | 'Flood'
          })} required data-id="element-144" />
              <Select label="Loan Type (imbus__Loan_Type__c)" options={loanTypeOptions} value={formData.loanType} onChange={e => setFormData({
            ...formData,
            loanType: e.target.value as 'Purchase' | 'Refinance'
          })} required data-id="element-145" />
            </div>

            {/* Row 2: Insurance Company with inline create */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-146">
              <div data-id="element-147">
                <div className="flex items-center justify-between mb-1" data-id="element-148">
                  <label className="block text-sm font-medium" style={{
                color: 'var(--nmhl-text-primary)'
              }} data-id="element-149">
                    Insurance Company
                  </label>
                  <button type="button" onClick={() => setShowNewInsuranceCompanyModal(true)} className="inline-flex items-center text-xs font-medium transition-colors hover:opacity-80" style={{
                color: 'var(--nmhl-burgundy)'
              }} data-id="element-150">
                    <PlusIcon className="w-3 h-3 mr-1" data-id="element-151" />
                    Create New
                  </button>
                </div>
                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
              '--tw-ring-color': 'var(--nmhl-burgundy)'
            } as React.CSSProperties} value={formData.insuranceCompanyId} onChange={e => setFormData({
              ...formData,
              insuranceCompanyId: e.target.value
            })} required data-id="element-152">
                  {insuranceCompanyOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-153">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Row 3: Agent with inline create */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-154">
              <div data-id="element-155">
                <div className="flex items-center justify-between mb-1" data-id="element-156">
                  <label className="block text-sm font-medium" style={{
                color: 'var(--nmhl-text-primary)'
              }} data-id="element-157">
                    Agent Name
                  </label>
                  <button type="button" onClick={() => setShowNewAgentModal(true)} className="inline-flex items-center text-xs font-medium transition-colors hover:opacity-80" style={{
                color: 'var(--nmhl-burgundy)'
              }} data-id="element-158">
                    <PlusIcon className="w-3 h-3 mr-1" data-id="element-159" />
                    Create New
                  </button>
                </div>
                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
              '--tw-ring-color': 'var(--nmhl-burgundy)'
            } as React.CSSProperties} value={formData.agentContactId} onChange={e => handleAgentContactChange(e.target.value)} required data-id="element-160">
                  {agentContactOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-161">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Auto-populated agent fields */}
            {formData.agentContactId && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-162">
                <div data-id="element-163">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-164">Agent Phone</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-165">
                    {formData.agentPhone || '—'}
                  </p>
                </div>
                <div data-id="element-166">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-167">Agent Email</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-168">
                    {formData.agentEmail || '—'}
                  </p>
                </div>
              </div>}

            {/* Row 4: Chosen Lender → auto-pull mortgagee clause */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-169">
              <Select label="Chosen Lender (for Mortgagee Clause)" options={chosenLenderOptions} value={formData.chosenLenderId} onChange={e => handleChosenLenderChange(e.target.value)} required data-id="element-170" />
            </div>

            {/* Auto-populated mortgagee clause */}
            {formData.mortgageeClause && <div className="mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-171">
                <p className="text-xs text-slate-500 mb-1" data-id="element-172">
                  Mortgagee Clause (auto-pulled from Lender)
                </p>
                <p className="text-sm font-medium text-slate-900" data-id="element-173">
                  {formData.mortgageeClause}
                </p>
              </div>}

            {/* Row 5: Effective Date + Policy Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-174">
              <Input label={effectiveDateLabel} type="date" value={formData.hoiEffectiveDate} onChange={e => setFormData({
            ...formData,
            hoiEffectiveDate: e.target.value
          })} required data-id="element-175" />
              <Input label="Policy Number (optional)" value={formData.policyNumber} onChange={e => setFormData({
            ...formData,
            policyNumber: e.target.value
          })} placeholder="Enter if available" data-id="element-176" />
            </div>

            {/* Borrowers Authorization */}
            <div className="mt-4" data-id="element-177">
              <div className="flex items-center justify-between mb-2" data-id="element-178">
                <label className="block text-sm font-medium" style={{
              color: 'var(--nmhl-text-primary)'
            }} data-id="element-179">
                  Borrowers Authorization
                </label>
                <span className="text-xs font-medium" style={{
              color: 'var(--nmhl-burgundy)'
            }} data-id="element-180">
                  Required before send
                </span>
              </div>
              {formData.insuranceBorrowersAuthAttached && !insuranceAuthFile ? <div className="flex items-center space-x-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-181">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-182" />
                  <span className="text-sm font-medium text-emerald-700" data-id="element-183">
                    Authorization attached
                  </span>
                </div> : <div data-id="element-184">
                  <FileUpload onFileSelect={handleInsuranceAuthUpload} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-id="element-185" />
                  {!formData.insuranceBorrowersAuthAttached && <div className="flex items-center space-x-2 mt-2 text-amber-600" data-id="element-186">
                      <AlertCircleIcon className="w-4 h-4" data-id="element-187" />
                      <span className="text-xs" data-id="element-188">
                        Authorization must be attached before the order can be
                        sent
                      </span>
                    </div>}
                </div>}
            </div>
          </div>}

        {/* === TITLE SECTION === */}
        {orderType === 'title' && <div className="border-t border-slate-200 pt-6" data-id="element-189">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-190">
              Title Details
            </h3>

            {/* Opportunity Fields (read-only display) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-191">
              <div data-id="element-192">
                <p className="text-xs text-slate-500 mb-1" data-id="element-193">
                  DOB (from Opportunity)
                </p>
                <Input type="date" value={formData.borrowerDob} onChange={e => setFormData({
              ...formData,
              borrowerDob: e.target.value
            })} disabled data-id="element-194" />
              </div>
              <div data-id="element-195">
                <p className="text-xs text-slate-500 mb-1" data-id="element-196">
                  Last 4 SSN (from Opportunity)
                </p>
                <Input value={formData.borrowerLast4SSN} onChange={e => setFormData({
              ...formData,
              borrowerLast4SSN: e.target.value
            })} placeholder="••••" maxLength={4} disabled data-id="element-197" />
              </div>
            </div>

            {/* Chosen Lender → auto-pull mortgagee clause */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-198">
              <Select label="Chosen Lender (for Mortgagee Clause)" options={titleLenderOptions} value={formData.titleChosenLenderId} onChange={e => handleTitleLenderChange(e.target.value)} required data-id="element-199" />
            </div>

            {/* Auto-populated mortgagee clause */}
            {formData.titleMortgageeClause && <div className="mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-200">
                <p className="text-xs text-slate-500 mb-1" data-id="element-201">
                  Mortgagee Clause (auto-pulled from Lender)
                </p>
                <p className="text-sm font-medium text-slate-900" data-id="element-202">
                  {formData.titleMortgageeClause}
                </p>
              </div>}

            {/* Title Company with inline create */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-203">
              <div data-id="element-204">
                <div className="flex items-center justify-between mb-1" data-id="element-205">
                  <label className="block text-sm font-medium" style={{
                color: 'var(--nmhl-text-primary)'
              }} data-id="element-206">
                    Title Company
                  </label>
                  <button type="button" onClick={() => setShowNewTitleCompanyModal(true)} className="inline-flex items-center text-xs font-medium transition-colors hover:opacity-80" style={{
                color: 'var(--nmhl-burgundy)'
              }} data-id="element-207">
                    <PlusIcon className="w-3 h-3 mr-1" data-id="element-208" />
                    Create New
                  </button>
                </div>
                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
              '--tw-ring-color': 'var(--nmhl-burgundy)'
            } as React.CSSProperties} value={formData.titleCompanyId} onChange={e => setFormData({
              ...formData,
              titleCompanyId: e.target.value
            })} required data-id="element-209">
                  {titleCompanyOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-210">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Title Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-211">
              <Select label="Title Contact" options={titleContactOptions} value={formData.titleContactId} onChange={e => handleTitleContactChange(e.target.value)} required data-id="element-212" />
            </div>

            {/* Auto-populated contact fields */}
            {formData.titleContactId && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-slate-50 rounded-lg p-4 border border-slate-200" data-id="element-213">
                <div data-id="element-214">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-215">Title Phone</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-216">
                    {formData.titleContactPhone || '—'}
                  </p>
                </div>
                <div data-id="element-217">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-218">Title Email</p>
                  <p className="text-sm font-medium text-slate-900" data-id="element-219">
                    {formData.titleContactEmail || '—'}
                  </p>
                </div>
              </div>}
          </div>}

        {/* === APPRAISAL SECTION === */}
        {orderType === 'appraisal' && <div className="border-t border-slate-200 pt-6" data-id="element-220">
            <h3 className="text-lg font-medium text-slate-900 mb-4" data-id="element-221">
              Appraisal Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-222">
              <Input label="Appraiser" value={formData.appraiser} onChange={e => setFormData({
            ...formData,
            appraiser: e.target.value
          })} required data-id="element-223" />
              <Input label="Order Date" type="date" value={formData.appraisalOrderDate} onChange={e => setFormData({
            ...formData,
            appraisalOrderDate: e.target.value
          })} required data-id="element-224" />
              <div className="md:col-span-2" data-id="element-225">
                <Input label="Property Address" value={formData.propertyAddress} onChange={e => setFormData({
              ...formData,
              propertyAddress: e.target.value
            })} required data-id="element-226" />
              </div>
              <Input label="ValuLink ID" value={formData.valuLinkId} onChange={e => setFormData({
            ...formData,
            valuLinkId: e.target.value
          })} placeholder="Auto-generated" disabled data-id="element-227" />
            </div>
          </div>}

        <div className="flex justify-end space-x-3 pt-6 border-t border-slate-200" data-id="element-228">
          <Button type="button" variant="secondary" onClick={onCancel} data-id="element-229">
            Cancel
          </Button>
          <Button type="submit" variant="primary" data-id="element-230">
            {order ? 'Update Order' : 'Create Order'}
          </Button>
        </div>
      </form>

      {/* Inline Create Payoff Company Modal */}
      <Modal isOpen={showNewCompanyModal} onClose={() => setShowNewCompanyModal(false)} title="Create Payoff Company" size="md" data-id="element-231">
        <div className="space-y-4" data-id="element-232">
          <Input label="Company Name" value={newCompany.name} onChange={e => setNewCompany({
          ...newCompany,
          name: e.target.value
        })} required data-id="element-233" />
          <Input label="Phone" type="tel" value={newCompany.phone} onChange={e => setNewCompany({
          ...newCompany,
          phone: e.target.value
        })} required data-id="element-234" />
          <Input label="Email" type="email" value={newCompany.email} onChange={e => setNewCompany({
          ...newCompany,
          email: e.target.value
        })} required data-id="element-235" />
          <div className="flex items-center space-x-3" data-id="element-236">
            <input type="checkbox" id="isAutomated" checked={newCompany.isAutomated} onChange={e => setNewCompany({
            ...newCompany,
            isAutomated: e.target.checked
          })} className="w-4 h-4 rounded border-slate-300" data-id="element-237" />
            <label htmlFor="isAutomated" className="text-sm font-medium text-slate-700" data-id="element-238">
              Is Automated
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200" data-id="element-239">
            <Button type="button" variant="secondary" onClick={() => setShowNewCompanyModal(false)} data-id="element-240">
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleCreateCompany} data-id="element-241">
              Create Company
            </Button>
          </div>
        </div>
      </Modal>

      {/* Inline Create Agent Contact Modal */}
      <Modal isOpen={showNewAgentModal} onClose={() => setShowNewAgentModal(false)} title="Create Agent Contact" size="md" data-id="element-242">
        <div className="space-y-4" data-id="element-243">
          <Input label="Agent Name" value={newAgent.name} onChange={e => setNewAgent({
          ...newAgent,
          name: e.target.value
        })} required data-id="element-244" />
          <Input label="Phone" type="tel" value={newAgent.phone} onChange={e => setNewAgent({
          ...newAgent,
          phone: e.target.value
        })} required data-id="element-245" />
          <Input label="Email" type="email" value={newAgent.email} onChange={e => setNewAgent({
          ...newAgent,
          email: e.target.value
        })} required data-id="element-246" />
          <Select label="Insurance Company" options={insuranceCompanyOptions} value={newAgent.companyId || ''} onChange={e => setNewAgent({
          ...newAgent,
          companyId: e.target.value
        })} data-id="element-247" />
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200" data-id="element-248">
            <Button type="button" variant="secondary" onClick={() => setShowNewAgentModal(false)} data-id="element-249">
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleCreateAgent} data-id="element-250">
              Create Agent
            </Button>
          </div>
        </div>
      </Modal>

      {/* Inline Create Insurance Company Modal */}
      <Modal isOpen={showNewInsuranceCompanyModal} onClose={() => setShowNewInsuranceCompanyModal(false)} title="Create Insurance Company" size="md" data-id="element-251">
        <div className="space-y-4" data-id="element-252">
          <Input label="Company Name" value={newInsuranceCompany.name} onChange={e => setNewInsuranceCompany({
          ...newInsuranceCompany,
          name: e.target.value
        })} required data-id="element-253" />
          <Input label="Phone" type="tel" value={newInsuranceCompany.phone} onChange={e => setNewInsuranceCompany({
          ...newInsuranceCompany,
          phone: e.target.value
        })} required data-id="element-254" />
          <Input label="Email" type="email" value={newInsuranceCompany.email} onChange={e => setNewInsuranceCompany({
          ...newInsuranceCompany,
          email: e.target.value
        })} required data-id="element-255" />
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200" data-id="element-256">
            <Button type="button" variant="secondary" onClick={() => setShowNewInsuranceCompanyModal(false)} data-id="element-257">
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleCreateInsuranceCompany} data-id="element-258">
              Create Company
            </Button>
          </div>
        </div>
      </Modal>

      {/* Inline Create Title Company Modal */}
      <Modal isOpen={showNewTitleCompanyModal} onClose={() => setShowNewTitleCompanyModal(false)} title="Create Title Company" size="md" data-id="element-259">
        <div className="space-y-4" data-id="element-260">
          <Input label="Company Name" value={newTitleCompany.name} onChange={e => setNewTitleCompany({
          ...newTitleCompany,
          name: e.target.value
        })} required data-id="element-261" />
          <Input label="Phone" type="tel" value={newTitleCompany.phone} onChange={e => setNewTitleCompany({
          ...newTitleCompany,
          phone: e.target.value
        })} required data-id="element-262" />
          <Input label="Email" type="email" value={newTitleCompany.email} onChange={e => setNewTitleCompany({
          ...newTitleCompany,
          email: e.target.value
        })} required data-id="element-263" />
          <Input label="Address" value={newTitleCompany.address || ''} onChange={e => setNewTitleCompany({
          ...newTitleCompany,
          address: e.target.value
        })} data-id="element-264" />
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200" data-id="element-265">
            <Button type="button" variant="secondary" onClick={() => setShowNewTitleCompanyModal(false)} data-id="element-266">
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleCreateTitleCompany} data-id="element-267">
              Create Company
            </Button>
          </div>
        </div>
      </Modal>
    </>;
}